import { useViewContext, useViewEntranceSettled } from '../../view-context'
import { EmptyState, EmptyStateDescription, EmptyStateIcon } from '../../components/meeting/empty-state'
import { View } from '../../components/view'
import { ActionViewHeader } from './nested/header'
import { ActionViewBody } from './nested/body'
import { ActionChatDrawer } from './nested/chat-drawer'
import { UnsavedChangesDialog } from './nested/unsaved-changes-dialog'
import { useActionViewModel, useLatchQuestionKind, type ActionViewContext } from './use-action-view-model'
import { useDismissActionChatDrawer } from './use-action-chat-drawer'
import { useActionChatAttachment } from './use-action-chat-attachment'
import { useUnsavedChangesGuard } from './use-unsaved-changes-guard'
import { useDraftAction } from './use-draft-action'
import { actionViewBottomFadeTint } from './action-view-state'
import { useActionLock, useIsActionHeld } from './use-action-lock'
import { LockedActionBody } from './nested/locked-action-body'
import { CreditsLockedPage } from '../../components/sidepanel/plan-banners/credits-lock'
import { useTrackStagedActionShown } from '../../hooks/staged-actions/use-track-staged-action-shown'
import { Search } from 'lucide-react'

/**
 * Action View (HIG-5824): the native drill-in View for a drafted action —
 * title block, property chips, full body, and the state-colored footer chrome.
 * The footer controls (execute CTA / question card / busy pill) mount in the
 * sidepanel floating bar (`ActionViewFloatingRegion`) so they share the row
 * above the chat input with the rest of the panel chrome; the scoped chat
 * input itself stays panel chrome (`SidepanelChatInput`'s staged-action mode).
 */
export function ActionView() {
  const { context } = useViewContext<ActionViewContext>()
  const model = useActionViewModel(context?.actionId)
  useTrackStagedActionShown(model.action, 'action-view')
  useLatchQuestionKind(context, model.state)
  useActionChatAttachment(model)
  useUnsavedChangesGuard(model.isEditable ? [model.draft] : [])
  const lock = useActionLock(model.action)
  // A locked action can't be drafted.
  const draftControl = useDraftAction(useIsActionHeld(model.action) ? undefined : model.action)
  // The drawer's list mounts only once the slide-in settles, like the Chat
  // View — markdown rendering for every message would block the first frames.
  const entranceSettled = useViewEntranceSettled()
  const dismissDrawer = useDismissActionChatDrawer()

  if (model.isLoading) {
    return (
      <View>
        <View.Skeleton />
      </View>
    )
  }

  if (!model.action) {
    return (
      <View>
        <div className="flex flex-1 flex-col items-center justify-center">
          <EmptyState>
            <EmptyStateIcon>
              <Search />
            </EmptyStateIcon>
            <EmptyStateDescription>
              {model.error ? 'Failed to load action' : 'Action no longer exists'}
            </EmptyStateDescription>
          </EmptyState>
        </div>
      </View>
    )
  }

  if (lock?.kind === 'credits') {
    return (
      <View>
        <CreditsLockedPage lock={lock.lock} from="action view">
          <div className="flex flex-1 flex-col gap-2 p-2">
            <ActionViewHeader model={model} allowEditing={false} className="p-3" />
            <ActionViewBody model={model} />
          </div>
        </CreditsLockedPage>
      </View>
    )
  }

  if (lock?.kind === 'tier') {
    return (
      <View>
        <LockedActionBody
          action={model.action}
          tier={lock.tier}
          dismissible
          header={<ActionViewHeader model={model} allowEditing={false} className="p-3" />}
        />
      </View>
    )
  }

  return (
    <View bottomFadeTint={actionViewBottomFadeTint(model)} className="gap-2 p-2">
      <ActionViewHeader model={model} className="p-3" />
      <ActionViewBody model={model} draftControl={draftControl} />
      <UnsavedChangesDialog action={model.action} draft={model.draft} />
      {/* The chat drawer (HIG-5977): the scoped thread slides up over the view
          as a bottom sheet while this entry is in its chat state — the action
          itself stays visible behind it. Mounted (closed) whenever a thread
          exists at all, regardless of the flag, so open and close both
          transition; a legacy edit has no conversation and nothing to open. */}
      {context?.actionId && model.chatId && (
        <ActionChatDrawer
          actionId={context.actionId}
          chatId={model.chatId}
          editState={model.editState}
          open={Boolean(context.chat)}
          entranceSettled={entranceSettled}
          onDismiss={dismissDrawer}
        />
      )}
    </View>
  )
}
